"use client";

import React, { createRef } from "react";
import { IBaseComponentProps } from "../props/IBaseComponentProps";
import { IValidationProps } from "../props/IValidationProps";
import { IValidationStringProps } from "../props/IValidationStringProps";
import { safeMenuMenuItem } from "./NSMenuButton";
import { INSBoxBaseLayoutProps } from "./NSBoxBaseLayout";
import { INSBox } from "./INSBox";
import { NSRow } from "./NSRow";
import { NSBoxString } from "./NSBoxString";
import { BaseVariableSchema } from "namirasoft-schema";
import { NSBoxSchemaBase } from "./NSBoxSchemaBase";
import { NSBoxBaseLayoutRecursive } from "./NSBoxBaseLayoutRecursive";
import { NSDialog } from "./NSDialog";
import { NSBoxDynamic } from "./NSBoxDynamic";
import { NSBoxBoolean } from "./NSBoxBoolean";
import { NSSpace, NSSpaceSizeType } from "./NSSpace";
import { NSLine } from "./NSLine";
import { NSTitle } from "./NSTitle";
import { NSButtonBlue } from "./NSButtonBlue";
import { NSBoxTextArea } from "./NSBoxTextArea";
import { NSBox } from "./NSBox";
import { SetTimeouService } from "namirasoft-core";
import Styles from "./NSBoxSchemaVariable.module.css";

export interface NSBoxSchemaVariableProps extends IBaseComponentProps, IValidationProps, IValidationStringProps, INSBoxBaseLayoutProps<NSBoxSchemaVariable, BaseVariableSchema>
{
	name: string;
	depth?: number;
}

export interface NSBoxSchemaVariableState
{
	show_value: BaseVariableSchema | null;
	show_error: string | null;
}

export class NSBoxSchemaVariable extends React.Component<NSBoxSchemaVariableProps, NSBoxSchemaVariableState> implements INSBox
{
	private service = new SetTimeouService();
	private NSBoxString_Name = createRef<NSBoxString>();
	private NSBoxString_Default = createRef<NSBoxString>();
	private NSBoxBoolean_Multiple = createRef<NSBoxBoolean>();
	private NSBoxSchemaBase_Type = createRef<NSBoxSchemaBase>();
	private NSBoxTextArea_Value = createRef<NSBoxTextArea>();
	private NSBoxDynamic_Value = createRef<NSBoxDynamic>();
	NSBoxBaseLayoutRecursive_Main = createRef<NSBoxBaseLayoutRecursive>();

	constructor(props: NSBoxSchemaVariableProps)
	{
		super(props);
		this.state = { show_value: null, show_error: null };
		this.isEmpty = this.isEmpty.bind(this);
		this.getError = this.getError.bind(this);
		this.getValue = this.getValue.bind(this);
		this.setValue = this.setValue.bind(this);
		this.onChanged = this.onChanged.bind(this);
	}
	isEmpty(value: BaseVariableSchema | null)
	{
		if (value)
			if (value.name || value.type || value.defaultValue)
				return false;
		return true;
	}
	getError(): string | null
	{
		let ans = this.NSBoxString_Name.current?.getError() ||
			this.NSBoxSchemaBase_Type.current?.getError() ||
			this.NSBoxString_Default.current?.getError();
		return ans ?? null;
	}
	getValue(checkError: boolean = true): BaseVariableSchema | null
	{
		return NSBoxBaseLayoutRecursive.checkGetValue(this, checkError, () =>
		{
			let name = this.NSBoxString_Name.current?.getValue(checkError);
			let type = this.NSBoxSchemaBase_Type.current?.getValue(checkError);
			let defaultValue = this.NSBoxString_Default.current?.getValue(checkError) ?? "";
			let multiple = this.NSBoxBoolean_Multiple.current?.getValue(checkError) ?? false;
			if (name && type)
			{
				let ans = new BaseVariableSchema(name, type, defaultValue);
				if (multiple)
					ans.tags["multiple"] = "true";
				return ans;
			}
			return null;
		});
	}
	setValue(value: BaseVariableSchema | null, callback?: () => void): void
	{
		this.NSBoxString_Name.current?.setValue(value?.name ?? null);
		this.NSBoxSchemaBase_Type.current?.setValue(value?.type ?? null);
		this.NSBoxString_Default.current?.setValue(value?.defaultValue ?? null);
		this.NSBoxBoolean_Multiple.current?.setValue(value?.tags["multiple"] === "true");
		this.props.onChanged?.(this);
		callback?.();
	}
	private onChanged()
	{
		this.props.onChanged?.(this);
	}
	override render()
	{
		let menu = safeMenuMenuItem(this.props, (items) =>
		{
			items.push({
				title: "Show Result",
				icon: "https://static.namirasoft.com/image/concept/detail/blue.svg",
				onClick: () =>
				{
					try
					{
						let value = this.getValue(true);
						this.setState({ show_value: value });
					} catch (error)
					{
					}
				}
			});
		});

		return (
			<NSBoxBaseLayoutRecursive
				ref={this.NSBoxBaseLayoutRecursive_Main}
				id={this.props.id}
				{...this.props}
				depth={this.props.depth ?? 0}
				menu={menu}
			>
				<NSRow
				>
					<NSBoxString
						ref={this.NSBoxString_Name}
						required={true}
						title="Name"
						onChanged={this.onChanged}
						classList={[Styles.ns_box_schema_variable_name]}
					/>
					<NSBoxString
						ref={this.NSBoxString_Default}
						required={false}
						title="Default"
						onChanged={this.onChanged}
						classList={[Styles.ns_box_schema_variable_default]}
					/>
					<NSBoxBoolean
						ref={this.NSBoxBoolean_Multiple}
						required
						title="Multiple"
						onChanged={this.onChanged}
						classList={[Styles.ns_box_schema_variable_multiple]}
					/>
				</NSRow>
				<NSBoxSchemaBase
					ref={this.NSBoxSchemaBase_Type}
					name={this.props.name + "_type"}
					required
					title="Type"
					onChanged={this.onChanged}
					style={{ width: "100%" }}
					depth={(this.props.depth ?? 0) + 1}
				/>
				{
					this.state.show_value &&
					<NSDialog
						center_dialog
						onClose={() => this.setState({ show_value: null })}
					>
						<NSTitle
							title="Output"
						/>
						<NSBoxDynamic
							ref={this.NSBoxDynamic_Value}
							name="output"
							title={this.state.show_value.name}
							variable={this.state.show_value}
							style={{ width: "100%", height: "100%" }}
						/>
						<NSSpace size={NSSpaceSizeType.LARGE} />
						<NSLine />
						<NSSpace size={NSSpaceSizeType.SMALL} />
						{
							this.state.show_error === "" &&
							<NSTitle title="It's valid" style={{ color: "green" }} />
						}
						{
							this.state.show_error &&
							<NSTitle title="It's Not valid" style={{ color: "red" }} />
						}
						<NSRow>
							<NSButtonBlue
								title='Get Value'
								onClick={{
									action: () =>
									{
										let value = this.NSBoxDynamic_Value.current?.getValue(false);
										this.NSBoxTextArea_Value.current?.setValue(JSON.stringify(value));
									},
									showLoading: false
								}}
							/>
							<NSButtonBlue
								title='Set Value'
								onClick={{
									action: () =>
									{
										let value = this.NSBoxTextArea_Value.current?.getValue();
										if (value)
											this.NSBoxDynamic_Value.current?.setValue(JSON.parse(value));
									},
									showLoading: false
								}}
							/>
							<NSButtonBlue
								title='Check Error'
								onClick={{
									action: () =>
									{
										try
										{
											this.NSBoxDynamic_Value.current?.getValue(true);
											this.setState({ show_error: "" });
										} catch (error)
										{
											this.setState({ show_error: (error as Error).message });
										}
										this.service.setTimeoutIfNotCalledAgain(() => this.setState({ show_error: null }), 3000);
									},
									showLoading: false
								}}
							/>
						</NSRow>

						<NSBoxTextArea
							ref={this.NSBoxTextArea_Value}
							title='Value'
							required={false}
							style={{ width: NSBox.width.triple }}
						></NSBoxTextArea>
					</NSDialog>
				}
			</NSBoxBaseLayoutRecursive>
		);
	}
}